import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Input } from '@mono/ui-components';
import { MessageSquare, Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useCreateProjectChat, useProjectChats } from '../../hooks/useProjectChats';
import { ErrorBoundary } from '../ErrorBoundary';
import { ChatCard } from './ChatCard';
import { NewChatButton } from './NewChatButton';
/**
 * Error fallback for ProjectChatsSection
 */ function ProjectChatsSectionError() {
    return /*#__PURE__*/ _jsxs("section", {
        className: "flex flex-col h-full",
        "data-testid": "project-chats-section",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 mb-4",
                children: [
                    /*#__PURE__*/ _jsx(MessageSquare, {
                        className: "w-5 h-5 text-gray-500"
                    }),
                    /*#__PURE__*/ _jsx("h2", {
                        className: "text-lg font-semibold text-gray-900",
                        children: "Chats"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "text-center py-12 text-red-500",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "font-medium",
                        children: "Something went wrong loading chats"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-500 mt-2",
                        children: "Please try refreshing the page"
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        onClick: ()=>window.location.reload(),
                        className: "mt-4",
                        size: "sm",
                        children: "Refresh"
                    })
                ]
            })
        ]
    });
}
/**
 * Displays the list of project chats with create action.
 * Handles search, sorting, and role-based create button visibility.
 * Wrapped with ErrorBoundary for graceful error handling.
 */ export function ProjectChatsSection({ projectId, userRole }) {
    return /*#__PURE__*/ _jsx(ErrorBoundary, {
        fallback: /*#__PURE__*/ _jsx(ProjectChatsSectionError, {}),
        children: /*#__PURE__*/ _jsx(ProjectChatsSectionInner, {
            projectId: projectId,
            userRole: userRole
        })
    });
}
/**
 * Inner component with the actual implementation.
 */ function ProjectChatsSectionInner({ projectId, userRole }) {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const { data, isLoading, isError, error } = useProjectChats(projectId, {
        sort: 'createdAt',
        order: 'desc',
        search: searchQuery || undefined
    });
    const createChat = useCreateProjectChat();
    const canCreate = userRole === 'owner' || userRole === 'member';
    const handleCreateChat = async ()=>{
        try {
            const response = await createChat.mutateAsync({
                projectId,
                data: {}
            });
            toast.success('Chat created successfully');
            navigate(`/project/${projectId}/chat/${response.chat.chatId}`);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to create chat';
            toast.error(message);
        }
    };
    const handleChatClick = (chatId)=>{
        navigate(`/assistant/${chatId}`);
    };
    const chats = data?.chats || [];
    // IMPORTANT: Keep header and search ALWAYS mounted to preserve focus (Lesson #8)
    // Only conditionally render the results area
    return /*#__PURE__*/ _jsxs("section", {
        className: "flex flex-col h-full",
        "aria-labelledby": "chats-section-heading",
        "data-testid": "project-chats-section",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between mb-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(MessageSquare, {
                                className: "w-5 h-5 text-gray-400"
                            }),
                            /*#__PURE__*/ _jsxs("h2", {
                                id: "chats-section-heading",
                                className: "text-lg font-medium text-gray-600",
                                children: [
                                    "Chats ",
                                    isLoading ? '...' : `(${data?.count ?? 0})`
                                ]
                            })
                        ]
                    }),
                    canCreate && /*#__PURE__*/ _jsx(NewChatButton, {
                        onSelect: handleCreateChat,
                        disabled: createChat.isPending
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "relative mb-4",
                children: [
                    /*#__PURE__*/ _jsx(Search, {
                        className: "absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                    }),
                    /*#__PURE__*/ _jsx(Input, {
                        type: "text",
                        placeholder: "Search chats...",
                        value: searchQuery,
                        onChange: (e)=>setSearchQuery(e.target.value),
                        className: "pl-9 h-9",
                        "data-testid": "chat-search-input",
                        "aria-label": "Search chats"
                    })
                ]
            }),
            isError ? /*#__PURE__*/ _jsxs("div", {
                className: "text-center py-8 text-red-500",
                "data-testid": "chats-error",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm",
                        children: "Failed to load chats"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-gray-400 mt-1",
                        children: error?.message || 'Please try refreshing the page'
                    })
                ]
            }) : isLoading ? /*#__PURE__*/ _jsx("div", {
                className: "space-y-3",
                children: [
                    1,
                    2,
                    3
                ].map((i)=>/*#__PURE__*/ _jsx("div", {
                        className: "h-24 bg-gray-100 rounded-md animate-pulse"
                    }, i))
            }) : chats.length === 0 ? /*#__PURE__*/ _jsx(EmptyState, {
                canCreate: canCreate,
                onCreateClick: handleCreateChat,
                hasSearchQuery: !!searchQuery,
                isCreating: createChat.isPending
            }) : /*#__PURE__*/ _jsx("div", {
                className: "space-y-3",
                "data-testid": "chat-list",
                children: chats.map((chat)=>/*#__PURE__*/ _jsx(ChatCard, {
                        chat: chat,
                        onClick: ()=>handleChatClick(chat.chatId)
                    }, chat.chatId))
            })
        ]
    });
}
function EmptyState({ canCreate, onCreateClick, hasSearchQuery, isCreating }) {
    const [inputValue, setInputValue] = useState('');
    if (hasSearchQuery) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "text-center py-8 text-gray-500",
            "data-testid": "no-chats-message",
            children: [
                /*#__PURE__*/ _jsx(MessageSquare, {
                    className: "w-10 h-10 mx-auto mb-2 text-gray-300"
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-sm",
                    children: "No chats match your search"
                })
            ]
        });
    }
    const handleKeyDown = (e)=>{
        if (e.key === 'Enter' && !e.shiftKey && inputValue.trim()) {
            e.preventDefault();
            onCreateClick();
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col h-full",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex-1 flex flex-col items-center justify-center py-8",
                children: [
                    /*#__PURE__*/ _jsx(MessageSquare, {
                        className: "w-10 h-10 text-gray-300 mb-3"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-500 text-center",
                        children: canCreate ? 'Start a conversation' : 'No chats yet'
                    })
                ]
            }),
            canCreate && /*#__PURE__*/ _jsx("div", {
                className: "mt-auto",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "relative",
                    children: [
                        /*#__PURE__*/ _jsx(Input, {
                            placeholder: "Ask a question...",
                            value: inputValue,
                            onChange: (e)=>setInputValue(e.target.value),
                            onKeyDown: handleKeyDown,
                            disabled: isCreating,
                            className: "pr-10",
                            "data-testid": "empty-chat-input"
                        }),
                        /*#__PURE__*/ _jsx(NewChatButton, {
                            onSelect: onCreateClick,
                            disabled: isCreating,
                            variant: "inline"
                        })
                    ]
                })
            })
        ]
    });
}
